import React, { useEffect, useState } from "react";
import { FaArrowLeft, FaBuilding, FaEdit, FaFileAlt, FaMapMarkerAlt } from "react-icons/fa";
import axiosInstance from "../components/api/axiosInstance";
import PortalLocationSelects from "./PortalLocationSelects";
import {
  COMPANY_READONLY,
  COMPANY_DETAILS_READONLY,
  COMPANY_EDITABLE,
  COMPANY_ADDRESS,
  COMPANY_ADDRESS_EDITABLE,
  displayCompanyValue,
} from "./companyProfileFields";

function buildCompanyForm(data) {
  const initial = {};
  [...COMPANY_EDITABLE, ...COMPANY_ADDRESS_EDITABLE].forEach(([key]) => {
    initial[key] = data?.[key] || "";
  });
  initial.countryId = data?.Country_Id ? String(data.Country_Id) : "";
  initial.stateId = data?.State_Id ? String(data.State_Id) : "";
  initial.cityId = data?.City_Id ? String(data.City_Id) : "";
  return initial;
}

function buildSavePayload(form) {
  const payload = {};
  [...COMPANY_EDITABLE, ...COMPANY_ADDRESS_EDITABLE].forEach(([key]) => {
    payload[key] = typeof form[key] === "string" ? form[key].trim() || null : form[key];
  });
  payload.Country_Id = form.countryId ? Number(form.countryId) : null;
  payload.State_Id = form.stateId ? Number(form.stateId) : null;
  payload.City_Id = form.cityId ? Number(form.cityId) : null;
  return payload;
}

function DetailItem({ label, value, children }) {
  return (
    <div className="cportal-profile-view-item">
      <span className="cportal-profile-view-label">{label}</span>
      <span className="cportal-profile-view-value">{children ?? displayCompanyValue(value)}</span>
    </div>
  );
}

function statusBadgeClass(status) {
  const s = String(status || "").toLowerCase();
  if (s === "active") return "cportal-badge cportal-badge--success";
  if (s === "inactive") return "cportal-badge cportal-badge--danger";
  return "cportal-badge";
}

function SectionTitle({ icon: Icon, children }) {
  return (
    <h3 className="cportal-detail-section-title cportal-company-section-title">
      {Icon ? <Icon className="cportal-detail-section-icon" aria-hidden="true" /> : null}
      {children}
    </h3>
  );
}

function CustomerCompanyProfile() {
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await axiosInstance.get("/client/company");
        if (!active) return;
        const data = res.data?.data || {};
        setCompany(data);
        setForm(buildCompanyForm(data));
      } catch (_) {
        if (active) setError("Could not load company profile.");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const setField = (name, value) => setForm((f) => ({ ...f, [name]: value }));

  const openEdit = () => {
    setForm(buildCompanyForm(company));
    setMessage({ type: "", text: "" });
    setEditing(true);
  };

  const cancelEdit = () => {
    setEditing(false);
    setForm(buildCompanyForm(company));
    setMessage({ type: "", text: "" });
  };

  const saveCompany = async (e) => {
    e.preventDefault();
    setMessage({ type: "", text: "" });
    setSaving(true);
    try {
      const res = await axiosInstance.put("/client/company", buildSavePayload(form));
      const data = res.data?.data;
      if (data) {
        setCompany(data);
        setForm(buildCompanyForm(data));
      }
      setEditing(false);
      setMessage({ type: "success", text: "Company profile updated." });
    } catch (err) {
      setMessage({
        type: "error",
        text: err.response?.data?.message || "Could not update company profile.",
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="cportal-page">
        <p className="cportal-muted">Loading…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="cportal-page">
        <div className="cportal-alert cportal-alert-error">{error}</div>
      </div>
    );
  }

  if (editing) {
    return (
      <div className="cportal-page">
        <button type="button" className="cportal-back-link" onClick={cancelEdit}>
          <FaArrowLeft /> Back to company profile
        </button>

        <form className="cportal-card" style={{ marginTop: 16 }} onSubmit={saveCompany}>
          <div className="cportal-card-head">
            <div className="cportal-card-head-title">
              <span className="cportal-card-title-icon" aria-hidden="true">
                <FaBuilding />
              </span>
              <h2>Edit company profile</h2>
            </div>
          </div>

          {message.text && (
            <div className={`cportal-alert cportal-alert-${message.type === "error" ? "error" : "success"}`}>
              {message.text}
            </div>
          )}

          <SectionTitle icon={FaFileAlt}>Company details</SectionTitle>
          <div className="cportal-form-grid cportal-form-grid--3">
            {COMPANY_DETAILS_READONLY.map(([key, label]) => (
              <div className="cportal-field" key={key}>
                <label>{label}</label>
                <input value={displayCompanyValue(company?.[key])} disabled />
              </div>
            ))}
            {COMPANY_EDITABLE.map(([key, label]) => (
              <div className="cportal-field" key={key}>
                <label>{label}</label>
                <input value={form[key] || ""} onChange={(e) => setField(key, e.target.value)} />
              </div>
            ))}
            {COMPANY_READONLY.map(([key, label]) => (
              <div className="cportal-field" key={key}>
                <label>{label}</label>
                <input value={displayCompanyValue(company?.[key])} disabled />
              </div>
            ))}
          </div>

          <SectionTitle icon={FaMapMarkerAlt}>Address</SectionTitle>
          <div className="cportal-form-grid cportal-form-grid--3">
            <div className="cportal-field">
              <label>Address line 1</label>
              <input
                value={form.Address_Line_1 || ""}
                onChange={(e) => setField("Address_Line_1", e.target.value)}
              />
            </div>
            <div className="cportal-field">
              <label>Address line 2</label>
              <input
                value={form.Address_Line_2 || ""}
                onChange={(e) => setField("Address_Line_2", e.target.value)}
              />
            </div>
            <PortalLocationSelects
              countryId={form.countryId}
              stateId={form.stateId}
              cityId={form.cityId}
              onChange={({ countryId, stateId, cityId }) =>
                setForm((f) => ({ ...f, countryId, stateId, cityId }))
              }
            />
            <div className="cportal-field">
              <label>Zip code</label>
              <input value={form.Zip_Code || ""} onChange={(e) => setField("Zip_Code", e.target.value)} />
            </div>
          </div>

          <div className="cportal-form-actions">
            <button
              type="button"
              className="cportal-btn cportal-btn-ghost"
              onClick={cancelEdit}
              disabled={saving}
            >
              Cancel
            </button>
            <button type="submit" className="cportal-btn cportal-btn-primary" disabled={saving}>
              {saving ? "Saving…" : "Save changes"}
            </button>
          </div>
        </form>
      </div>
    );
  }

  const phone = company?.Company_Phone;
  const email = company?.Company_Email;
  const website = company?.Website;
  const websiteHref =
    website && String(website).trim()
      ? /^https?:\/\//i.test(website)
        ? website
        : `https://${website}`
      : null;

  return (
    <div className="cportal-page">
      <div className="cportal-card cportal-company-profile-card">
        <div className="cportal-card-head">
          <div className="cportal-card-head-title">
            <span className="cportal-card-title-icon" aria-hidden="true">
              <FaBuilding />
            </span>
            <h2>Company profile</h2>
          </div>
          <button type="button" className="cportal-btn cportal-btn-primary" onClick={openEdit}>
            <FaEdit aria-hidden="true" /> Edit
          </button>
        </div>

        {message.text && (
          <div className={`cportal-alert cportal-alert-${message.type === "error" ? "error" : "success"}`}>
            {message.text}
          </div>
        )}

        <SectionTitle icon={FaFileAlt}>Company details</SectionTitle>
        <div className="cportal-profile-view-grid">
          <DetailItem label="Company name" value={company?.Company_Name} />
          <DetailItem label="Company email" value={email}>
            {email ? (
              <a href={`mailto:${email}`} className="cportal-contact-link">
                {email}
              </a>
            ) : (
              "—"
            )}
          </DetailItem>
          <DetailItem label="Company phone" value={phone}>
            {phone ? (
              <a href={`tel:${String(phone).replace(/[^\d+]/g, "")}`} className="cportal-contact-link">
                {phone}
              </a>
            ) : (
              "—"
            )}
          </DetailItem>
          <DetailItem label="Website" value={website}>
            {websiteHref ? (
              <a href={websiteHref} target="_blank" rel="noopener noreferrer" className="cportal-contact-link">
                {website}
              </a>
            ) : (
              "—"
            )}
          </DetailItem>
          <DetailItem label="Website domain name" value={company?.Website_Domain_Name} />
          <DetailItem label="Domain" value={company?.Domain_Name} />
          <DetailItem label="Status" value={company?.Status}>
            <span className={statusBadgeClass(company?.Status)}>
              {displayCompanyValue(company?.Status)}
            </span>
          </DetailItem>
        </div>

        <SectionTitle icon={FaMapMarkerAlt}>Address</SectionTitle>
        <div className="cportal-profile-view-grid">
          {COMPANY_ADDRESS.map(([key, label]) => (
            <DetailItem key={key} label={label} value={company?.[key]} />
          ))}
        </div>
      </div>
    </div>
  );
}

export default CustomerCompanyProfile;
